import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X, LogIn, UserPlus, LogOut, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "./Logo";
import { NAV } from "@/constants/testIds";

const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/contestants", label: "Contestants" },
  { to: "/apply", label: "Apply" },
  { to: "/episodes", label: "Episodes" },
  { to: "/governor-bago", label: "Governor Bago" },
  { to: "/sponsors", label: "Sponsors" },
  { to: "/news", label: "News" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/contestants?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Top Bar */}
      <header className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="flex items-center gap-2 sm:gap-4 px-3 sm:px-4 lg:px-8 py-3">
          {/* Sidebar toggle (left-top) */}
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid={NAV.toggleSidebar}
            aria-label="Toggle navigation"
            className="h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-sm border border-white/10 hover:border-[#F26A1F] hover:text-[#F26A1F] transition-colors"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>

          {/* Search bar with logo embedded */}
          <form onSubmit={onSearch} className="flex-1 min-w-0 max-w-2xl">
            <div className="flex items-center gap-2 sm:gap-3 h-10 px-2 sm:px-3 rounded-sm border border-white/10 bg-white/[0.03] focus-within:border-[#F26A1F] transition-colors">
              <Link to="/" className="shrink-0 flex items-center" aria-label="Agrovest home">
                <Logo className="h-6 sm:h-7 w-auto" testId={NAV.logo} />
              </Link>
              <div className="hidden sm:block h-5 w-px bg-white/10" />
              <Search size={16} className="text-white/40 hidden sm:block shrink-0" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid={NAV.searchInput}
                placeholder="Search..."
                className="flex-1 min-w-0 bg-transparent outline-none text-sm placeholder:text-white/30"
              />
            </div>
          </form>

          {/* Right side - Register / Login / User */}
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {user ? (
              <>
                <span className="hidden md:inline text-xs uppercase tracking-[0.15em] text-white/60 px-3">
                  <User size={14} className="inline mr-1" />
                  {user.name}
                </span>
                <button
                  onClick={logout}
                  data-testid={NAV.logout}
                  className="h-10 px-3 sm:px-4 inline-flex items-center gap-2 rounded-sm border border-white/10 hover:border-[#F26A1F] hover:text-[#F26A1F] text-xs uppercase tracking-[0.15em] font-bold transition-colors"
                >
                  <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid={NAV.loginLink}
                  className="hidden sm:inline-flex h-10 px-4 items-center gap-2 rounded-sm border border-white/10 hover:border-white/40 text-xs uppercase tracking-[0.15em] font-bold transition-colors"
                >
                  <LogIn size={14} /> Login
                </Link>
                <Link
                  to="/register"
                  data-testid={NAV.registerLink}
                  className="h-10 px-3 sm:px-5 inline-flex items-center gap-2 rounded-sm bg-[#F26A1F] hover:bg-[#D95A15] text-white text-xs uppercase tracking-[0.15em] font-bold transition-colors shadow-[0_0_0_0_rgba(242,106,31,0.6)] hover:shadow-[0_0_24px_2px_rgba(242,106,31,0.45)]"
                >
                  <UserPlus size={14} /> <span className="hidden xs:inline sm:inline">Register</span>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Left fixed sidebar */}
      <aside
        data-testid={NAV.sidebar}
        className={`fixed top-0 left-0 z-50 h-full w-72 max-w-[85vw] bg-black border-r border-white/10 transform transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <Logo className="h-10 w-auto" />
          <button
            onClick={() => setOpen(false)}
            className="h-9 w-9 inline-flex items-center justify-center rounded-sm border border-white/10 hover:border-[#F26A1F]"
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        </div>
        <nav className="p-2">
          {NAV_ITEMS.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `group flex items-center justify-between px-4 py-3.5 border-b border-white/5 text-sm transition-colors ${
                  isActive
                    ? "text-[#F26A1F]"
                    : "text-white/70 hover:text-white"
                }`
              }
            >
              <span className="tracking-tight">{it.label}</span>
              <span className="opacity-0 group-hover:opacity-100 text-[#1E8E3E] transition-opacity">
                →
              </span>
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-5 border-t border-white/10 text-xs text-white/40">
          <div className="uppercase tracking-[0.2em] text-[#F26A1F] font-bold mb-1">
            Season 1
          </div>
          <div>Show Me The Money</div>
          <div className="mt-1">₦20 Million Grand Prize</div>
        </div>
      </aside>

      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
      )}

      <main className="pt-[64px]">{children}</main>
      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer className="mt-24 border-t border-white/10 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid md:grid-cols-4 gap-10">
        <div>
          <Logo className="h-14 w-auto mb-4" />
          <p className="text-sm text-white/50 leading-relaxed">
            Nigeria's first and most ambitious agri-entertainment platform.
            37 contestants. 37 states. 37,000 lives changed.
          </p>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-[#F26A1F] mb-4">
            The Show
          </h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/about" className="hover:text-white">About ARTS</Link></li>
            <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
            <li><Link to="/contestants" className="hover:text-white">Contestants</Link></li>
            <li><Link to="/episodes" className="hover:text-white">Episodes</Link></li>
            <li><Link to="/bago-ai" className="hover:text-white">Governor Bago</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-[#1E8E3E] mb-4">
            Connect
          </h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/sponsors" className="hover:text-white">Sponsors</Link></li>
            <li><Link to="/news" className="hover:text-white">News</Link></li>
            <li><Link to="/gallery" className="hover:text-white">Gallery</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
            <li><Link to="/register" className="hover:text-white">Register</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-white/80 mb-4">
            Headquarters
          </h4>
          <p className="text-sm text-white/60 leading-relaxed">
            Agrovest Media House<br />
            Abuja, FCT, Nigeria<br />
            +234-7064056982<br />
            info@agrovesttvshow.com.ng
          </p>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-white/40 tracking-wider">
        © {new Date().getFullYear()} Agrovest Reality TV Show. All Rights Reserved.
      </div>
    </footer>
  );
}
